import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { getPlans } from '../../api/subscription.api';
import type { Plan } from '../../types/subscription';
import type { TrialConfig } from '../../types/trial';
import { formatPrice, formatPricePerMonth } from '../../utils/formatPrice';
import { devicesUpTo, formatDays } from '../../utils/pluralize';
import {
  CDN_TITLE,
  PLANS_SUBTITLE,
  PLAN_FEATURE_UNLIMITED,
  cdnPackagesLine,
  cdnPlanFeatureValue,
  trialFaqAnswer,
} from '../../texts/cdn';
import styles from './LandingPage.module.css';

/* ===== FAQ Data ===== */
interface FaqEntry {
  question: string;
  /** Функция — когда ответ зависит от конфига триала (числа приходят с бекенда) */
  answer: string | ((trialConfig: TrialConfig | null) => string);
}

const faqData: FaqEntry[] = [
  {
    question: 'Как подключиться?',
    answer:
      'После оплаты вы получите ссылку подключения. Добавьте её в приложение на вашем устройстве — и защищённое соединение заработает. Рекомендуем Happ: он есть для iPhone, Android, Windows, macOS, Linux и TV, а российские сервисы в нём работают напрямую. Подойдут также v2RayTun, INCY и v2rayNG. Пошаговая инструкция с кнопкой «Открыть в приложении» есть в личном кабинете.',
  },
  {
    question: 'Какие устройства поддерживаются?',
    answer:
      'Norion работает на Windows, macOS, Linux, Android и iOS. Для каждой платформы есть совместимый клиент. В подписку входят 2 устройства, ключ для дополнительного устройства можно докупить отдельно.',
  },
  {
    question: 'Есть ли пробный период?',
    answer: trialFaqAnswer,
  },
  {
    question: 'Насколько это безопасно?',
    answer:
      'Мы используем современные протоколы шифрования. Ваш трафик защищён от перехвата. Мы не храним логи подключений и не отслеживаем вашу активность.',
  },
  {
    question: 'Как оплатить подписку?',
    answer:
      'Мы принимаем банковские карты (Visa, MasterCard, МИР) и оплату через СБП. После оплаты подписка активируется мгновенно.',
  },
];

/* ===== Feature icons (28px for 64px container) ===== */
function SpeedIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ZapIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="16 12 12 8 8 12" />
      <line x1="12" y1="16" x2="12" y2="8" />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

/* ===== FAQ Item ===== */
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.faqItem}>
      <button
        className={styles.faqQuestion}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {question}
        <span className={`${styles.faqChevron} ${open ? styles.open : ''}`}>
          <ChevronDown />
        </span>
      </button>
      <div className={`${styles.faqAnswer} ${open ? styles.open : ''}`}>
        <p className={styles.faqAnswerContent}>{answer}</p>
      </div>
    </div>
  );
}

/* ===== Plan feature row ===== */
function PlanFeature({ children, highlighted = false }: { children: ReactNode; highlighted?: boolean }) {
  return (
    <div className={`${styles.planFeature} ${highlighted ? styles.planFeatureKey : ''}`}>
      <span className={styles.planFeatureIcon}>
        <CheckIcon />
      </span>
      <span>{children}</span>
    </div>
  );
}

/* ===== Features Data ===== */
const features = [
  {
    icon: <SpeedIcon />,
    title: 'Высокая скорость',
    desc: 'Серверы с пропускной способностью до 1 Гбит/с. Стриминг, игры и работа без задержек.',
  },
  {
    icon: <ShieldIcon />,
    title: 'Безопасность',
    desc: 'Современное шифрование трафика. Мы не храним логи и не отслеживаем вашу активность.',
  },
  {
    icon: <ZapIcon />,
    title: 'Простота',
    desc: 'Получите ключ, добавьте его в приложение — и готово.',
  },
  {
    icon: <SupportIcon />,
    title: 'Живая поддержка',
    desc: 'Вам отвечает человек, а не бот. Поможем с настройкой и решим проблему.',
  },
];

/** Месяцев в тарифе: 30/90/180 дней → 1/3/6 */
function planMonths(durationDays: number): number {
  return Math.max(1, Math.round(durationDays / 30));
}

/** Экономия в % относительно самого короткого тарифа в пересчёте на месяц */
function savingsPercent(plan: Plan, base: Plan): number {
  const basePerMonth = base.priceKopecks / planMonths(base.durationDays);
  if (basePerMonth <= 0) return 0;
  const perMonth = plan.priceKopecks / planMonths(plan.durationDays);
  return Math.round((1 - perMonth / basePerMonth) * 100);
}

/* ===== Landing Page ===== */
export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { trialConfig, cdnPackages, fetchTrialConfig, fetchCdnPackages } = useSubscriptionStore();

  // Тарифы — из публичного /plans; null = ещё грузим, ошибка/пусто = заглушка
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [plansFailed, setPlansFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPlans()
      .then((list) => {
        if (!cancelled) setPlans(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setPlansFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Конфиг триала и прайс докупки — публичные, кэшируются в сторе на весь сеанс
  useEffect(() => {
    fetchTrialConfig();
    fetchCdnPackages();
  }, [fetchTrialConfig, fetchCdnPackages]);

  const sortedPlans = plans ? [...plans].sort((a, b) => a.durationDays - b.durationDays) : [];
  const basePlan = sortedPlans[0];
  const plansUnavailable = plansFailed || (plans !== null && sortedPlans.length === 0);

  const trialDays = trialConfig ? formatDays(trialConfig.durationDays) : null;

  return (
    <>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={`${styles.heroBgOrb} ${styles.heroBgOrb1}`} />
          <div className={`${styles.heroBgOrb} ${styles.heroBgOrb2}`} />
          <div className={`${styles.heroBgOrb} ${styles.heroBgOrb3}`} />
        </div>
        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>
            Быстрый и безопасный{' '}
            <span className={styles.heroGradient}>доступ</span> к интернету
          </h1>
          <p className={styles.heroSubtitle}>
            Защитите свои данные с Norion. Быстрое подключение,
            без ограничений скорости и трафика. Полная конфиденциальность
            в сети.
          </p>
          <div className={styles.heroCta}>
            <Link to="/register">
              <Button size="lg">Попробовать бесплатно</Button>
            </Link>
            <a href="#features">
              <Button variant="secondary" size="lg">
                Узнать больше
              </Button>
            </a>
          </div>
          <div className={styles.heroTrust}>
            <span className={styles.heroTrustIcon}>
              <ShieldCheckIcon />
            </span>
            <span>
              {trialDays ? `Пробный период ${trialDays}` : 'Пробный период'} &mdash; без привязки карты
            </span>
          </div>
        </div>
      </section>

      {/* Start paths — right after hero, only for guests */}
      {!isAuthenticated && (
        <section className={styles.startSection}>
          <div className={styles.sectionInner}>
            <h2 className={styles.sectionTitle}>Готовы начать?</h2>
            <p className={styles.sectionSubtitle}>
              Зарегистрируйтесь и получите {trialDays ? `${trialDays} бесплатно` : 'пробный период бесплатно'}
            </p>
            <div className={styles.startPaths}>
              <Card className={styles.startPathCard}>
                <div className={styles.startPathIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
                <h3 className={styles.startPathTitle}>Регистрация на сайте</h3>
                <p className={styles.startPathDesc}>
                  Создайте аккаунт, активируйте пробный период и управляйте подпиской в личном кабинете
                </p>
                <Link to="/register">
                  <Button variant="primary" fullWidth>Создать аккаунт</Button>
                </Link>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className={styles.features} id="features">
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>Почему Norion?</h2>
          <p className={styles.sectionSubtitle}>
            Всё, что нужно для быстрого и безопасного интернета, в одном сервисе
          </p>
          <div className={styles.featuresGrid}>
            {features.map((f) => (
              <Card key={f.title} className={styles.featureCard}>
                <div className={styles.featureIcon}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureDesc}>{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className={styles.pricing} id="pricing">
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>Тарифы</h2>
          <p className={styles.sectionSubtitle}>
            {PLANS_SUBTITLE}.
            <span className={styles.extraKeyNote}>
              Можно докупить ключ на дополнительное устройство.
            </span>
          </p>

          {plansUnavailable ? (
            <p className={styles.pricingUnavailable}>Тарифы временно недоступны</p>
          ) : plans === null ? (
            <div className={styles.pricingLoading}>
              <Spinner size="lg" />
            </div>
          ) : (
            <div className={styles.pricingGrid}>
              {sortedPlans.map((plan) => {
                // Тот же признак «популярного», что и на странице тарифов в кабинете
                const isPopular = plan.durationDays === 90;
                const months = planMonths(plan.durationDays);
                const savings = basePlan ? savingsPercent(plan, basePlan) : 0;

                return (
                  <Card
                    key={plan.id}
                    variant={isPopular ? 'highlighted' : 'default'}
                    className={
                      isPopular ? styles.pricingCardPopular : styles.pricingCard
                    }
                  >
                    {isPopular && (
                      <span className={styles.popularBadge}>Популярный</span>
                    )}
                    <h3 className={styles.planName}>{plan.name}</h3>
                    <div className={styles.planPrice}>
                      {formatPrice(plan.priceKopecks)}
                    </div>
                    <p className={styles.planPeriod}>
                      {months > 1
                        ? formatPricePerMonth(plan.priceKopecks, months)
                        : 'за 1 месяц'}
                    </p>
                    <p className={styles.planSavings}>
                      {savings > 0 ? `Экономия ${savings}%` : ' '}
                    </p>
                    <div className={styles.planFeatures}>
                      <PlanFeature>{devicesUpTo(plan.maxDevices)}</PlanFeature>
                      <PlanFeature>Все локации</PlanFeature>
                      <PlanFeature>{PLAN_FEATURE_UNLIMITED}</PlanFeature>
                      {plan.cdnTrafficBytesPerMonth > 0 && (
                        <PlanFeature highlighted>
                          {CDN_TITLE}:{' '}
                          <strong className={styles.planFeatureValue}>
                            {cdnPlanFeatureValue(plan.cdnTrafficBytesPerMonth)}
                          </strong>
                        </PlanFeature>
                      )}
                      <PlanFeature>Высокая скорость</PlanFeature>
                    </div>
                    <Link to="/register" className={styles.planButton}>
                      <Button
                        variant={isPopular ? 'primary' : 'secondary'}
                        fullWidth
                      >
                        Подключить
                      </Button>
                    </Link>
                  </Card>
                );
              })}
            </div>
          )}

          {cdnPackages && cdnPackages.packages.length > 0 && (
            <p className={styles.pricingNote}>{cdnPackagesLine(cdnPackages)}</p>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.faq}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>Частые вопросы</h2>
          <p className={styles.sectionSubtitle}>
            Ответы на популярные вопросы о сервисе
          </p>
          <div className={styles.faqList}>
            {faqData.map((item) => (
              <FAQItem
                key={item.question}
                question={item.question}
                answer={typeof item.answer === 'function' ? item.answer(trialConfig) : item.answer}
              />
            ))}
          </div>
        </div>
      </section>

    </>
  );
}
